import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../components/modal/actions';
import { openLoader, closeLoader } from '../../components/loader/actions';
import {
  transformSearchCriteriaBeforeReportPost,
} from '../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// // feature trailer-activity
import * as A from './actions';
import {
  report as trailersReport,
  columnSettings as trailerColumnSettings,
} from './settings/trailer-list';
//////////////////////////////////////////////////

function* getTrailerActivityListSaga() {
  try {
    yield put(A.setListLoading(true));

    const enterpriseGuid = yield select(makeSelectCurrentBranchGuid());

    const options = { params: { enterpriseGuid } };

    const res = yield call(sendRequest, 'get', endpointsMap.trailerActivityListList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getTrailerActivityListSaga fail');
    }

    yield put(A.setListLoading(false));
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getTrailerActivityListSaga exception');
  }
}

function* createTrailerActivityListSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const enterpriseGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: {
        ...payload,
        enterpriseGuid,
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.trailerActivityList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.createTrailerActivityListSuccess(data));
      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'createTrailerActivityListSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createTrailerActivityListSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* deleteItemRequest({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'delete', endpointsMap.getTrailerActivityListItem(payload));

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.deleteItemSuccess(payload));

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'deleteItemRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'deleteItemRequest fail');
  } finally {
    yield put(closeLoader());
  }
}

const requiredFields = [
  GC.GRC.NEXT_TEL_GUID,
  GC.GRC.LATEST_TEL_GUID,
  GC.GRC.FLEET_TRAILER_GUID,
  GC.GRC.TRAILER_LATEST_LOCATION_GUID,
];

function* getDetailsByRowGuidRequest({ payload }) {
  try {
    const {
      pagination,
      titleOrderFields,
      titleFilterParams,
      trailerActivityListGuid,
    } = payload;

    yield put(openLoader());

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const orderFields = G.isNotNilAndNotEmpty(titleOrderFields) ? R.values(titleOrderFields) : [];

    const searchCriteria = G.isNotNilAndNotEmpty(titleFilterParams) ? R.values(titleFilterParams) : [];

    const reqBody = {
      ...pagination,
      orderFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
      fields: G.addRequiredFields(R.prop('fields', trailersReport), requiredFields),
    };

    const options = {
      data: G.setSearchCriteria({ reqBody }),
      params: {
        [GC.FIELD_TRAILER_ACTIVITY_LIST_GUID]: trailerActivityListGuid,
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.trailerActivityListTrailerList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeLoader());
      yield put(A.getDetailsByRowGuidSuccess(R.assoc(
        GC.FIELD_TRAILER_ACTIVITY_LIST_GUID,
        trailerActivityListGuid,
        data,
      )));
    } else {
      yield call(G.handleFailResponse, res, 'getDetailsByRowGuidRequest fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'getDetailsByRowGuidRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* addTrailersToActivityListRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'post', endpointsMap.trailerActivityListTrailer, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const { trailerActivityListGuid } = payload;

      yield put(A.addTrailersToActivityListSuccess(trailerActivityListGuid));
      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'addTrailersToActivityListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'addTrailersToActivityListRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* updateTrailerActivityListRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'put', endpointsMap.trailerActivityList, { data: payload });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.updateTrailerActivityListSuccess(data));
      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'updateTrailerActivityListRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'updateTrailerActivityListRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* deleteTrailersRequest({ payload }) {
  try {
    yield put(openLoader());

    const { list, callback, trailerActivityListGuid } = payload;

    const options = {
      data: list,
    };

    const res = yield call(sendRequest, 'delete', endpointsMap.trailerActivityListTrailerMassDelete, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.resetTrailerActivityListDetails(trailerActivityListGuid));

      yield call(callback);
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'deleteTrailersRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'deleteTrailersRequest fail');
  } finally {
    yield put(closeLoader());
  }
}

function* exportTrailersRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const currentEnterprise = yield select(makeSelectCurrentBranchGuid());

    const { byReport, trailerActivityListGuid } = payload;

    const fields = byReport ? G.mapIndexed(
      (key, sequence) => {
        const { name } = R.prop(key, trailerColumnSettings);

        const displayName = G.isArray(name) ?
          R.compose(
            R.join(': '),
            R.map((locale) => G.getWindowLocale(locale, ' ')),
          )(name) :
          G.getWindowLocale(name, key);

        return { sequence, name: key, displayName };
      },
      R.keys(trailerColumnSettings),
    ) : [{
      sequence: 0,
      displayName: 'Unit Id',
      name: GC.GRC.FLEET_TRAILER_UNIT_ID,
    }];

    const options = {
      resType: 'arraybuffer',
      params: {
        format: 'xlsx',
        trailerActivityListGuid,
      },
      data: {
        fields,
        currentEnterprise,
        [GC.FIELD_REPORT_NAME]: byReport ? 'TRAILER_ACTIVITY_REPORT' : 'TRAILER_ACTIVITY_TRAILERS',
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.trailerActivityListTrailersExport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'exportTrailersRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'exportTrailersRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* synchronizeTrailerActivityRequest({ payload }) {
  try {
    const res = yield call(sendRequest, 'post', endpointsMap.getTrailerActivityListSyncEndpoint(payload));

    const { status } = res;

    if (G.isResponseFail(status)) {
      yield call(G.handleFailResponse, res, 'synchronizeTrailerActivityRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'synchronizeTrailerActivityRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* updateTrailerCommentsRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guid, comments } = payload;

    const data = { comments };

    const res = yield call(sendRequest, 'patch', endpointsMap.getTrailerActivityListTrailerEndpoint(guid), { data });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.updateTrailerCommentsSuccess(payload));
      yield put(closeModal());

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'updateTrailerCommentsRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'updateTrailerCommentsRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* visitTrailerActivityPageSaga({ payload }) {
  while (true) { // eslint-disable-line

    yield call(visitPageSaga, payload, GC.CHECK_VISIT_TRAILER_ACTIVITY_LIST_PAGE);

    yield put(openLoader());

    yield call(getTrailerActivityListSaga);

    yield put(closeLoader());
    break;
  }
}

function* trailerActivityListWatcherSaga() {
  // visit page
  yield takeLatest(GC.VISIT_TRAILER_ACTIVITY_LIST_PAGE, visitTrailerActivityPageSaga);
  yield takeLatest(A.deleteItemRequest, deleteItemRequest);
  yield takeLatest(A.deleteTrailersRequest, deleteTrailersRequest);
  yield takeLatest(A.exportTrailersRequest, exportTrailersRequest);
  yield takeLatest(A.getDetailsByRowGuidRequest, getDetailsByRowGuidRequest);
  yield takeLatest(A.updateTrailerCommentsRequest, updateTrailerCommentsRequest);
  yield takeLatest(A.createTrailerActivityListRequest, createTrailerActivityListSaga);
  yield takeLatest(A.addTrailersToActivityListRequest, addTrailersToActivityListRequest);
  yield takeLatest(A.updateTrailerActivityListRequest, updateTrailerActivityListRequest);
  yield takeLatest(A.synchronizeTrailerActivityRequest, synchronizeTrailerActivityRequest);
}

export default trailerActivityListWatcherSaga;
